import badge from "@/assets/pawkt-badge-256.png";
import { cn } from "@/lib/utils";

// Single source of truth for Pawkt APPLICATION branding (not tenant/business logos).
// Uses one canonical optimized badge asset + context-appropriate sizing variants.
const VARIANTS = {
  auth: { img: "h-12 w-12", text: "text-2xl", gap: "gap-3" },
  sidebar: { img: "h-9 w-9", text: "text-lg", gap: "gap-2.5" },
  topbar: { img: "h-8 w-8", text: "text-base", gap: "gap-2" },
  mobile: { img: "h-8 w-8", text: "text-lg", gap: "gap-2" },
  splash: { img: "h-20 w-20", text: "text-3xl", gap: "gap-4" },
  footer: { img: "h-7 w-7", text: "text-sm", gap: "gap-2" },
  compact: { img: "h-9 w-9", text: "", gap: "" },
};

export function PawktLogo({
  variant = "sidebar",
  withWordmark = true,
  subtitle,
  className,
  wordmarkClassName,
  subtitleClassName,
  decorative = false,
}) {
  const v = VARIANTS[variant] || VARIANTS.sidebar;
  const showWordmark = withWordmark && variant !== "compact";
  // When the readable wordmark is shown the badge is decorative to avoid
  // duplicate screen-reader output ("Pawkt Pawkt"). Icon-only gets the name.
  const imgDecorative = decorative || showWordmark;

  return (
    <div className={cn("flex items-center", v.gap, className)} data-testid="pawkt-logo">
      <img
        src={badge}
        alt={imgDecorative ? "" : "Pawkt"}
        aria-hidden={imgDecorative ? true : undefined}
        draggable={false}
        className={cn("shrink-0 select-none object-contain", v.img)}
      />
      {showWordmark && (
        <span className="flex flex-col leading-none">
          <span className={cn("font-display font-extrabold tracking-tight text-pawkt-text", v.text, wordmarkClassName)}>
            PAWKT
          </span>
          {subtitle && (
            <span className={cn("mt-0.5 text-[10px] uppercase tracking-[0.15em] text-pawkt-text-disabled", subtitleClassName)}>
              {subtitle}
            </span>
          )}
        </span>
      )}
    </div>
  );
}

export function PawktSplash({ label = "Loading your workspace" }) {
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-6 bg-white" data-testid="pawkt-splash">
      <div className="animate-pulse">
        <PawktLogo variant="splash" />
      </div>
      <div className="flex items-center gap-2 text-sm font-medium text-pawkt-text-secondary">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-pawkt-border-strong border-t-primary" />
        {label}…
      </div>
    </div>
  );
}
